import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, ComunidadNewsletterEdition } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { RichContent } from "./components/RichContent";
import { hasVocesBlock, isRichHtml } from "./richText";
import { VocesDeLaRedCard } from "./components/VocesDeLaRedCard";

const PAGE_SIZE = 5;

/** Archivo completo del newsletter — todas las ediciones publicadas, de más nueva a más
 *  vieja, cada una plegada por defecto ("Edición #2 — octubre: tema") y desplegable al
 *  hacer click para ver el contenido completo de esa edición puntual. Carga de a tandas —
 *  "Cargar más" pide la página siguiente en vez de traer todo el historial de una.
 *  `?edicion=N` abre esa edición ya desplegada ("Leer entero" desde la pantalla principal). */
export const NewsletterArchiveContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editions, setEditions] = useState<ComunidadNewsletterEdition[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    comunidadAdapter
      .getNewsletterPage(1, PAGE_SIZE)
      .then((result) => {
        setEditions(result.data);
        setHasMore(result.hasMore);
        const wanted = Number(searchParams.get("edicion"));
        const target = result.data.find((e) => e.editionNumber === wanted);
        if (target) setExpandedId(target.id);
      })
      .catch(() => toastUtils.error("No pudimos cargar el archivo. Probá de nuevo en un rato."))
      .finally(() => setLoading(false));
    // Solo al entrar: el `?edicion=` es la edición que se pidió abrir desde la pantalla principal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await comunidadAdapter.getNewsletterPage(nextPage, PAGE_SIZE);
      setEditions((prev) => [...prev, ...result.data]);
      setHasMore(result.hasMore);
      setPage(nextPage);
    } catch {
      toastUtils.error("No pudimos cargar más. Probá de nuevo en un rato.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-full w-full bg-[#E6DBFA]">
      {/* Columna de lectura: la tarjeta no ocupa toda la pantalla y el texto ocupa casi toda
          la tarjeta (antes: tarjeta de 1440px con el texto en una columna de 720px). */}
      <div className="mx-auto flex max-w-[960px] flex-col gap-6 px-4 py-6 md:px-8 md:py-10">

        <button
          type="button"
          onClick={() => navigate("/comunidad")}
          className="flex items-center gap-1.5 self-start text-sm font-semibold text-[#665090]"
        >
          <ArrowLeft size={16} />
          Volver a Comunidad
        </button>

        <h1 className="m-0 text-2xl font-extrabold text-[#2D2049]">Archivo del newsletter</h1>

        {loading ? (
          <div className="flex justify-center py-10"><CircularProgress /></div>
        ) : editions.length === 0 ? (
          <p className="m-0 text-sm text-gray-500">Todavía no hay ediciones publicadas.</p>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              {editions.map((edition) => {
                const expanded = expandedId === edition.id;
                return (
                  <div
                    key={edition.id}
                    className="overflow-hidden rounded-[20px] bg-white shadow-[0_6px_20px_rgba(109,87,188,0.08)]"
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : edition.id)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left md:px-12 md:py-5"
                    >
                      <span className="text-[15px] font-semibold text-[#2D2049]">
                        Edición #{edition.editionNumber} — {edition.month}: {edition.theme}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 text-[#665090] transition-transform ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {expanded &&
                      (isRichHtml(edition.body) ? (
                        <RichContent
                          html={edition.body}
                          className="px-5 pb-8 md:px-12 md:pb-10"
                          renderVoces={
                            edition.voicesQuestion
                              ? () => <VocesDeLaRedCard editionNumber={edition.editionNumber} />
                              : undefined
                          }
                        />
                      ) : (
                        <p className="m-0 whitespace-pre-line px-5 pb-8 text-base leading-relaxed text-[#2D2049] md:px-12 md:pb-10">
                          {edition.body}
                        </p>
                      ))}
                    {/* Si en el editor no se ubicó el bloque "Voces de la Red", el renglón va al final. */}
                    {expanded && edition.voicesQuestion && !hasVocesBlock(edition.body) && (
                      <div className="px-5 pb-8 md:px-12 md:pb-10">
                        <VocesDeLaRedCard editionNumber={edition.editionNumber} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <Button
                variant="outline"
                className="self-center border-[#C9BCE8] text-[#665090]"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Cargando…" : "Cargar más"}
              </Button>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default NewsletterArchiveContent;
